import { format } from 'date-fns';
import { formatCurrency } from '@/Utils/FnUtils';
import { EMissionType } from '../constants';
import type { CreateMissionFormType } from '../types';
import { buildRewardLevels } from './map-create-mission-form-to-payload';

export type MissionConfirmationData = {
  missionName: string;
  startDateLabel: string;
  endDateLabel: string;
  startTimeLabel: string;
  endTimeLabel: string;
  objectiveLabel: string;
  rewardLabel: string;
  audienceLabel: string;
  audienceItems: string[];
  creatorName: string;
};

type CityOption = {
  value: string;
  text: string;
};

type BuildMissionConfirmationDataParams = {
  formValues: CreateMissionFormType;
  creatorName: string;
  cityOptions: CityOption[];
  totalDeliverymen?: number;
};

const getCityNames = (
  cityIds: string[] | undefined,
  cityOptions: CityOption[],
) => {
  if (!cityIds?.length) return [];

  return cityIds
    .map(id => {
      const option = cityOptions.find(city => city.value === id);
      if (!option) return null;

      return option.text.split(' - ')[0]?.trim() || option.text;
    })
    .filter((name): name is string => !!name);
};

const getDeliverymanItems = (
  deliverymanIds: string[] | undefined,
  deliverymanCsv: File | null | undefined,
  totalDeliverymen: number | undefined,
) => {
  if (deliverymanCsv) {
    return [deliverymanCsv.name];
  }

  if (deliverymanIds?.length) {
    return deliverymanIds;
  }

  if (totalDeliverymen) {
    return [`${totalDeliverymen}`];
  }

  return [];
};

export const buildMissionConfirmationData = ({
  formValues,
  creatorName,
  cityOptions,
  totalDeliverymen,
}: BuildMissionConfirmationDataParams): MissionConfirmationData => {
  const rewardLevels = buildRewardLevels(formValues);
  const maxLevel = rewardLevels.length
    ? rewardLevels.reduce((current, level) =>
        level.conditionValue > current.conditionValue ? level : current,
      )
    : null;

  const isCityMission = formValues.targetAudience === EMissionType.CITY;

  return {
    missionName: formValues.missionName?.trim() || '—',
    startDateLabel: formValues.startDate
      ? format(formValues.startDate, 'dd/MM/yyyy')
      : '—',
    endDateLabel: formValues.endDate
      ? format(formValues.endDate, 'dd/MM/yyyy')
      : '—',
    startTimeLabel: formValues.startTime
      ? format(formValues.startTime, 'HH:mm')
      : '—',
    endTimeLabel: formValues.endTime
      ? format(formValues.endTime, 'HH:mm')
      : '—',
    objectiveLabel: maxLevel ? `${maxLevel.conditionValue} entregas` : '—',
    rewardLabel: maxLevel ? formatCurrency(maxLevel.rewardValue) : '—',
    audienceLabel: isCityMission ? 'Municípios' : 'Entregadores',
    audienceItems: isCityMission
      ? getCityNames(formValues.cityIds, cityOptions)
      : getDeliverymanItems(
          formValues.deliverymanIds,
          formValues.deliverymanCsv,
          totalDeliverymen,
        ),
    creatorName: creatorName.trim() || '—',
  };
};
